/**
 * DetachBarrierPassiveSkillEffect — Conjuration Tether path (Detach branch)
 * passive: barrier on successful shade return.
 *
 * Always-on passive. When a detached shade survives the full duration and
 * returns safely, the player gains a temporary barrier worth 3% of max HP
 * that decays linearly over 10 seconds. The shade came back intact —
 * something in the shared pool registered relief, and the body responded.
 *
 * The barrier absorbs damage before HP. It is not additive with itself:
 * re-triggering Detach and getting another successful return refreshes
 * the barrier to the new amount rather than stacking.
 *
 * Integration hooks:
 *   - `onDetachReturn(maxHp)` — called by the integration layer when
 *     the detached shade survives its full timer and HP is returned.
 *     Creates or refreshes the barrier.
 *   - The integration layer reads `barrierAmount` each frame. When the
 *     player takes damage, it calls `absorbDamage(rawDamage)` which
 *     returns the unabsorbed remainder to apply to HP.
 *
 * UpgradeEffectResolver keys (scoped to 'detach'):
 * - successfulReturnBarrierPercent: additive, base 0.03
 * - barrierDecayMs: max, base 10000
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Phantom (detach_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DetachBarrierPassiveDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface DetachBarrierPassiveConfig {
  /** Base barrier as a fraction of maxHp (0.03 = 3%). */
  readonly successfulReturnBarrierPercent: number;
  /** Base duration over which the barrier decays to zero, in ms. */
  readonly barrierDecayMs: number;
}

const DEFAULT_CONFIG: DetachBarrierPassiveConfig = {
  successfulReturnBarrierPercent: 0.03,  // spec § Phantom: 3%
  barrierDecayMs: 10000,                 // spec § Phantom: 10 seconds
};

/** Resolver scope — 'detach' for upgrades in the Detach branch. */
const RESOLVER_SCOPE = 'detach';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DetachBarrierPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'detach_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: DetachBarrierPassiveDeps;
  private readonly config: DetachBarrierPassiveConfig;

  /** Current barrier HP remaining. 0 = no barrier. */
  private _barrierAmount = 0;

  /** Barrier decay rate in HP per millisecond. */
  private _barrierDecayRate = 0;

  /** Total number of successful return triggers. */
  private _triggerCount = 0;

  constructor(deps: DetachBarrierPassiveDeps, config: Partial<DetachBarrierPassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Barrier-specific API ---

  /**
   * Resolve the effective barrier percent. Additive stacking: base 0.03
   * plus bonuses from future barrier-enhancing nodes.
   */
  getResolvedBarrierPercent(): number {
    const bonus = this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'successfulReturnBarrierPercent');
    return this.config.successfulReturnBarrierPercent + bonus;
  }

  /**
   * Resolve the effective decay duration. Max aggregation: the highest
   * value among base and any resolver overrides wins.
   */
  getResolvedDecayMs(): number {
    const override = this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'barrierDecayMs');
    return override !== undefined
      ? Math.max(this.config.barrierDecayMs, override)
      : this.config.barrierDecayMs;
  }

  /**
   * Called by the integration layer when the detached shade survives
   * its full duration and HP is returned to the player.
   *
   * Creates or refreshes the barrier — no stacking.
   *
   * @param maxHp  The player's maximum HP at the time of return.
   */
  onDetachReturn(maxHp: number): void {
    const percent = this.getResolvedBarrierPercent();
    const decayMs = this.getResolvedDecayMs();

    this._barrierAmount = maxHp * percent;
    this._barrierDecayRate = decayMs > 0 ? this._barrierAmount / decayMs : this._barrierAmount;
    this._triggerCount++;
  }

  /**
   * Absorb incoming damage through the barrier. Returns the unabsorbed
   * remainder that should be applied to the player's HP.
   *
   * @param rawDamage  The raw damage amount before barrier absorption.
   * @returns The damage that passes through the barrier (0 if fully absorbed).
   */
  absorbDamage(rawDamage: number): number {
    if (this._barrierAmount <= 0) return rawDamage;

    if (rawDamage <= this._barrierAmount) {
      this._barrierAmount -= rawDamage;
      return 0;
    }

    // Damage exceeds barrier — barrier breaks, remainder passes through
    const remainder = rawDamage - this._barrierAmount;
    this._barrierAmount = 0;
    this._barrierDecayRate = 0;
    return remainder;
  }

  // --- State getters ---

  /** Current barrier HP. */
  get barrierAmount(): number { return this._barrierAmount; }

  /** Whether the barrier is currently active. */
  get barrierActive(): boolean { return this._barrierAmount > 0; }

  /** Total number of successful return triggers. */
  get triggerCount(): number { return this._triggerCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(delta: number): void {
    if (this._barrierAmount > 0) {
      this._barrierAmount = Math.max(0, this._barrierAmount - this._barrierDecayRate * delta);
      if (this._barrierAmount <= 0) {
        this._barrierAmount = 0;
        this._barrierDecayRate = 0;
      }
    }
  }

  destroy(): void {
    this._barrierAmount = 0;
    this._barrierDecayRate = 0;
  }
}
